import {PrimaryCategory} from '@models/category';
import {CustomFieldTypes} from '@models/form-field';
import {ResourceCreateBase} from '@models/resource';
import {FormlyFieldConfig} from '@ngx-formly/core';
import {removeEmptyItems} from '@utilities/remove-empty-items';
import dayjs, {Dayjs} from 'dayjs';
import {lastValueFrom, Observable} from 'rxjs';

// Helper for date conversion on submission
export function normalizeEventDates(model: ResourceCreateBase) {
  if (model.resource_type === 'Event') {
    const starts = model.starts as unknown as Dayjs;
    const ends = model.ends as unknown as Dayjs;
    if (starts?.isValid?.()) model.starts = dayjs(starts).toISOString();
    if (ends?.isValid?.()) model.ends = dayjs(ends).toISOString();
  }
}

// Helper for base-64 encoding rich text fields (tinyMCE)
export function encodeRichTextFields(model: ResourceCreateBase, formFields: FormlyFieldConfig[]) {
  for (const field of formFields) {
    if (field.type === CustomFieldTypes.RICH_TEXT && typeof field.key === 'string' && field.key && model[field.key]) {
      const utf8Bytes = new TextEncoder().encode(model[field.key]);
      let binary = '';
      utf8Bytes.forEach(b => (binary += String.fromCharCode(b)));
      model[field.key] = btoa(binary);
    }
  }
}

/**
 * Returns true if the given form field matches the given type
 * AND the given model object has a value for the field in it.
 * May also add any additional condition.
 */
export const fieldAndModelMatchType = (
  fieldType: string,
  model: ResourceCreateBase,
  field: FormlyFieldConfig,
  additionalCondition: boolean = true,
) => {
  return (
    field.type === fieldType && typeof field.key === 'string' && field.key && model[field.key] && additionalCondition
  );
};

/**
 * For the given list of selected Primary, Secondary, and Tertiary Category ids,
 * returns a list of any Primary and Secondary Category ids to remove. Does not
 * mutate any of the given parameters.
 *
 * Walks the given tree Category tree. If any of the given selected category ids
 * belong to Primary or Secondary Categories, adds the ids of all their Tertiary
 * descendants to the given list of selected categories, then removes the parent
 * Category ids from the list.
 */
function selectTertiaryCatIds(allCats: PrimaryCategory[], selectedCatIds: string[]): string[] {
  const idsToRemove = [];
  const newCatIds = [...selectedCatIds];

  for (const primaryCat of allCats) {
    const addPrimaryGrandchildren = newCatIds.includes(primaryCat.id);

    for (const secondaryCat of primaryCat.children) {
      const addSecondaryChildren = newCatIds.includes(secondaryCat.id);

      if (addPrimaryGrandchildren || addSecondaryChildren) {
        for (const tertiaryCat of secondaryCat.children) {
          // Add the Tertiary Category
          newCatIds.push(tertiaryCat.id);
        }
      }

      if (addSecondaryChildren) {
        idsToRemove.push(secondaryCat.id);
      }
    }

    if (addPrimaryGrandchildren) {
      idsToRemove.push(primaryCat.id);
    }
  }

  // Finally, remove the Primary & Secondary Categories and return the result.
  return newCatIds.filter(c => !idsToRemove.includes(c));
}

/**
 * Mutates the given model object with any Primary and Secondary Category ids
 * replaced with a flat map of their Tertiary Categories
 */
export async function flatMapTertiaryCategories(model: ResourceCreateBase, formFields: FormlyFieldConfig[]) {
  for (const field of formFields) {
    if (!fieldAndModelMatchType('categorytree', model, field, !!field.props?.options)) continue;

    const key = field.key as string;
    const selectedCatIds = model[key] as string[];

    // Get the hierarchical tree of Categories
    const allCats = await lastValueFrom(field.props.options as Observable<PrimaryCategory[]>);

    // Replace the Primary & Secondary Categories and with Tertiary Categories.
    model[key] = selectTertiaryCatIds(allCats, selectedCatIds);
  }
}

// Transform form data to prep it for submission
export async function prepareFormPayload(model: ResourceCreateBase, formFields: any[]) {
  normalizeEventDates(model);
  const sanitized = removeEmptyItems(model);

  encodeRichTextFields(sanitized, formFields);

  await flatMapTertiaryCategories(sanitized, formFields);
  return sanitized;
}
